import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { API, CompanyDTO, ds_cm_Planning_retrieveAll_order, Order, PlanningDTO } from '../../../../services/api/api';
import { UtilService } from '../../../../services/util.service';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../../services/tag.service';
import { PlanningDTOAndTags } from '../../../../models/taggable.model';
import { OrderOption, SplitButtonClass } from '../../../../services/global/global-interfaces';
import { UserService } from '../../../../services/user.service';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalNavigationComponent } from '../../../../services/gui/modal/modal-navigation-component';
import { RequestClass } from '../../../../services/global/RequestClass';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { switchMap } from 'rxjs';
import { RoutingID } from '../../../../routes/sb-routing-enum';

type SplitButtonAction = 'delete';
type PlanningDTOAndTagsAndButtons = SplitButtonClass<PlanningDTOAndTags, SplitButtonAction>;

@Component({
  selector: 'sb-page-plannings-list',
  templateUrl: './plannings-list.component.html',
  styleUrls: ['./plannings-list.component.scss'],
  standalone: false,
})
export class PlanningsListComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorHandler = inject(ErrorService);
  private readonly modal = inject(ModalService);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UserService);
  protected readonly api = inject(API);
  protected readonly util = inject(UtilService);
  protected readonly tagService = inject(TagService);
  protected readonly permissionService = inject(PermissionService);

  isInModalContext: boolean = false;
  protected readonly ds_cm_Planning_retrieveAll_order = ds_cm_Planning_retrieveAll_order;

  // order Variables
  allOrderOptions: OrderOption<ds_cm_Planning_retrieveAll_order>[] = [
    { value: ds_cm_Planning_retrieveAll_order.CREATED_AT, direction: Order.asc, label: this.translate.instant('lang.globals.createdAt') },
    { value: ds_cm_Planning_retrieveAll_order.NAME, direction: Order.asc, label: this.translate.instant('lang.globals.name') },
    { value: ds_cm_Planning_retrieveAll_order.MODIFIED_AT, direction: Order.asc, label: this.translate.instant('lang.globals.modifiedAt') },
    { value: ds_cm_Planning_retrieveAll_order.CUSTOMER, direction: Order.asc, label: this.translate.instant('lang.globals.customer') },
  ];
  orderOption = signal({ ...this.allOrderOptions[0] });

  // global variables
  planningData: PlanningDTOAndTagsAndButtons[] = [];

  // filter panel variables
  selectedCompany: CompanyDTO | null = null;
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';

  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: {
      visibleWithoutChanges: true,
    },
  };

  ngOnInit(): void {
    this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
      if (company) {
        this.selectedCompany = company;
        this.requestDebounced();
      }
    });
  }

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ ...this.allOrderOptions[0] });
  }

  getOrder(): {
    by: ds_cm_Planning_retrieveAll_order;
    direction: Order;
  } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value) {
      this.resetOrderOption();
    }
    // if value equals column title
    return {
      by: currentOrderOption.value,
      direction: currentOrderOption.direction,
    };
  }

  setToOrderAndRefresh(pValue: ds_cm_Planning_retrieveAll_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  setToOrderOption(pValue: ds_cm_Planning_retrieveAll_order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === pValue);
    if (!pValue || !foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...foundOrderOption });
    }
  }

  // request methods
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }

    const sub = this.api
      .ds_cm_Planning_retrieveAll(
        {
          company: this.selectedCompany ? [this.selectedCompany.id] : null,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          order: this.getOrder(),
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(plannings => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Planning, plannings)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: planningData => {
          this.planningData = this.buildSplitButtons(planningData);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): SplitButtonClass<T, SplitButtonAction>[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.planning_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.planning,
          pathParams: [{ id: 'planningID', value: item?.id }],
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.planning_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.planningEdit,
          pathParams: [{ id: 'planningID', value: item?.id }],
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.planning_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.deletePlanning',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(planning: PlanningDTOAndTagsAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deletePlanning(planning);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  // methods
  deletePlanning(planning: PlanningDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.plannings.deletePlanning'),
        message: this.translate.instant('lang.plannings.deletePlanningQuestion', { planning: planning.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ds_cm_Planning_deleteById(planning.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.splicePlanning(planning);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  splicePlanning(planning: PlanningDTO) {
    const index = this.planningData.findIndex(x => x.id === planning.id);
    if (index !== -1) {
      this.planningData.splice(index, 1);
    }
  }

  selectPlanning(planning: PlanningDTO) {
    this.modalNavigation.result(planning);
  }

  // filter panel methods
  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.selectedCompany = filterMap.get('companies')?.[0];
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    const orderOption = filterMap.get('orderOption')?.[0];
    const orderDirection = filterMap.get('orderDirection')?.[0];
    this.setOrderOptionFromPanel(orderOption, orderDirection);
  }

  private setOrderOptionFromPanel(value: ds_cm_Planning_retrieveAll_order, pDirection: Order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === value);
    if (!foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    this.orderOption.set({ ...foundOrderOption, direction: pDirection });
  }
}
